<script>
  import { token } from "./store/token.js";
  import { handleApiResponse } from "./api/request.js";
  import { onMount } from "svelte";
  import { UPGRADE_PLAN_OPTIONS } from "./lib/planOptions.js";
  import { canAccess } from "./store/permissions.js";

  let searchQuery = "";
  let searchResults = [];
  let selectedWorkspace = null;
  let loading = false;
  let searchLoading = false;
  let error = null;
  let successMessage = null;
  let searchDebounceTimeout;

  // Upgrade form
  let selectedPlanId = "workspace-team-seats-yearly";
  let planQuantity = 5;
  let expiresAt = "";
  let upgradeLoading = false;

  // Trial extension form
  let extendDays = 14;
  let extendDate = "";
  let extendMode = "days";
  let trialLoading = false;

  const searchWorkspaces = async () => {
    if (!searchQuery.trim()) {
      searchResults = [];
      return;
    }

    searchLoading = true;
    error = null;

    try {
      const response = await fetch(
        `${process.env.API_ROOT}/admin/workspace/list?workspace_name=${encodeURIComponent(searchQuery.trim())}&limit=10`,
        {
          headers: { Authorization: "Bearer " + $token },
        }
      );

      const data = await handleApiResponse(response);
      searchResults = data.workspaces ?? data.records ?? [];
    } catch (e) {
      error = e.message;
      searchResults = [];
    } finally {
      searchLoading = false;
    }
  };

  const selectWorkspace = async (workspace) => {
    loading = true;
    error = null;
    searchResults = [];
    searchQuery = "";

    try {
      const response = await fetch(
        `${process.env.API_ROOT}/admin/workspace/${workspace.string_id}/detail`,
        {
          headers: { Authorization: "Bearer " + $token },
        }
      );

      // /detail returns { workspace, member_list, subscription, ... };
      // flatten it into the shape this page's template expects.
      const data = await handleApiResponse(response);
      const members = data.member_list ?? [];
      const owner = members.find((m) => m.workspace_role === "owner") ?? members[0];
      selectedWorkspace = {
        ...data.workspace,
        subscription: data.subscription,
        member_count: members.length,
        owner_email: owner?.email ?? null,
      };
    } catch (e) {
      error = e.message;
      selectedWorkspace = null;
    } finally {
      loading = false;
    }
  };

  const handleUpgrade = async () => {
    if (!selectedWorkspace) return;

    upgradeLoading = true;
    error = null;
    successMessage = null;

    try {
      const body = {
        plan_id: selectedPlanId,
        plan_quantity: parseInt(planQuantity, 10),
      };
      if (expiresAt) {
        body.expires_at = new Date(expiresAt).toISOString();
      }

      const response = await fetch(
        `${process.env.API_ROOT}/admin/workspace/${selectedWorkspace.string_id}/upgrade`,
        {
          method: "POST",
          headers: {
            Authorization: "Bearer " + $token,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(body),
        }
      );

      const data = await handleApiResponse(response);
      successMessage = data.message || "Workspace upgraded successfully";
      // Refresh workspace data
      await selectWorkspace({ string_id: selectedWorkspace.string_id });
    } catch (e) {
      error = e.message;
    } finally {
      upgradeLoading = false;
    }
  };

  const handleExtendTrial = async () => {
    if (!selectedWorkspace) return;

    trialLoading = true;
    error = null;
    successMessage = null;

    try {
      const body = {};
      if (extendMode === "days") {
        body.days = parseInt(extendDays, 10);
      } else {
        body.expires_at = new Date(extendDate).toISOString();
      }

      const response = await fetch(
        `${process.env.API_ROOT}/admin/workspace/${selectedWorkspace.string_id}/trial/extend`,
        {
          method: "POST",
          headers: {
            Authorization: "Bearer " + $token,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(body),
        }
      );

      const data = await handleApiResponse(response);
      successMessage = data.message || "Trial extended successfully";
      // Refresh workspace data
      await selectWorkspace({ string_id: selectedWorkspace.string_id });
    } catch (e) {
      error = e.message;
    } finally {
      trialLoading = false;
    }
  };

  const SHORTCUTS = [
    { label: "+14 days", body: { days: 14 } },
    { label: "+3 months", body: { months: 3 } },
    { label: "+6 months", body: { months: 6 } },
    { label: "+12 months", body: { months: 12 } },
  ];

  const extendTrialBy = async (body) => {
    if (!selectedWorkspace) return;

    trialLoading = true;
    error = null;
    successMessage = null;

    try {
      const response = await fetch(
        `${process.env.API_ROOT}/admin/workspace/${selectedWorkspace.string_id}/trial/extend`,
        {
          method: "POST",
          headers: {
            Authorization: "Bearer " + $token,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(body),
        }
      );

      const data = await handleApiResponse(response);
      successMessage = data.message || "Trial extended successfully";
      await selectWorkspace({ string_id: selectedWorkspace.string_id });
    } catch (e) {
      error = e.message;
    } finally {
      trialLoading = false;
    }
  };

  const clearSelection = () => {
    selectedWorkspace = null;
    error = null;
    successMessage = null;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Reference searchQuery so Svelte re-runs this block (and the debounced
  // search) whenever the query changes — not just once on mount.
  $: {
    searchQuery;
    clearTimeout(searchDebounceTimeout);
    searchDebounceTimeout = setTimeout(() => {
      searchWorkspaces();
    }, 300);
  }
</script>

<div class="container">
  <div class="ds-page-header">
    <h1 class="ds-page-title">Workspace Upgrades</h1>
  </div>

  {#if error}
    <div class="ds-alert ds-alert-error">
      {error}
      <button class="dismiss-btn" on:click={() => error = null}>Dismiss</button>
    </div>
  {/if}

  {#if successMessage}
    <div class="ds-alert ds-alert-success">
      {successMessage}
      <button class="dismiss-btn" on:click={() => successMessage = null}>Dismiss</button>
    </div>
  {/if}

  {#if !selectedWorkspace}
    <div class="ds-card search-section">
      <div class="search-box">
        <input
          type="text"
          bind:value={searchQuery}
          placeholder="Search workspace by name or ID..."
          class="ds-search"
        />
        {#if searchLoading}
          <div class="search-spinner"></div>
        {/if}
      </div>

      {#if searchResults.length > 0}
        <div class="search-results">
          {#each searchResults as workspace}
            <button class="result-item" on:click={() => selectWorkspace(workspace)}>
              <div class="result-title">{workspace.title || "Untitled Workspace"}</div>
              <div class="result-meta">
                <span class="result-id">{workspace.string_id}</span>
                <span class="result-owner">{workspace.email}</span>
              </div>
            </button>
          {/each}
        </div>
      {:else if searchQuery && !searchLoading}
        <div class="no-results">No workspaces found</div>
      {/if}
    </div>
  {:else}
    <button class="ds-btn ds-btn-secondary back-button" on:click={clearSelection}>
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M19 12H5M12 19l-7-7 7-7"/>
      </svg>
      Select Different Workspace
    </button>

    {#if loading}
      <div class="ds-loading loading-container">
        <div class="ds-spinner"></div>
        <div>Loading workspace details...</div>
      </div>
    {:else}
      <div class="ds-card workspace-card">
        <div class="ds-card-title workspace-title-row">
          <h2 class="workspace-title">{selectedWorkspace.title || "Untitled Workspace"}</h2>
          <div class="workspace-chips">
            <span class="ds-chip">{selectedWorkspace.string_id}</span>
            {#if selectedWorkspace.subscription}
              {#if selectedWorkspace.subscription.is_trial}
                <span class="ds-chip ds-chip-warn">Trial</span>
              {:else}
                <span class="ds-chip ds-chip-accent">{selectedWorkspace.subscription.plan_title || selectedWorkspace.subscription.plan_id || "Paid"}</span>
              {/if}
            {/if}
          </div>
        </div>
        <div class="ds-rows">
          <div class="ds-row">
            <span class="ds-row-label">Owner</span>
            <span class="ds-row-value">{selectedWorkspace.owner_email || "-"}</span>
          </div>
          <div class="ds-row">
            <span class="ds-row-label">Members</span>
            <span class="ds-row-value">{selectedWorkspace.member_count ?? "-"}</span>
          </div>
          <div class="ds-row">
            <span class="ds-row-label">Created</span>
            <span class="ds-row-value">{formatDate(selectedWorkspace.created_at)}</span>
          </div>
        </div>
      </div>

      {#if selectedWorkspace.subscription}
        <div class="ds-card subscription-card">
          <h3 class="ds-card-title">Current Subscription</h3>
          <div class="ds-rows">
            <div class="ds-row">
              <span class="ds-row-label">Plan</span>
              <span class="ds-row-value">{selectedWorkspace.subscription.plan_title || selectedWorkspace.subscription.plan_id || "-"}</span>
            </div>
            <div class="ds-row">
              <span class="ds-row-label">Seats</span>
              <span class="ds-row-value">{selectedWorkspace.subscription.plan_quantity ?? "-"}</span>
            </div>
            <div class="ds-row">
              <span class="ds-row-label">Status</span>
              <span class="ds-row-value">
                <span class="ds-chip" class:ds-chip-ok={selectedWorkspace.subscription.status === "active"}>
                  {selectedWorkspace.subscription.status || "-"}
                </span>
              </span>
            </div>
            <div class="ds-row">
              <span class="ds-row-label">Expires</span>
              <span class="ds-row-value">{formatDate(selectedWorkspace.subscription.expires_at)}</span>
            </div>
            <div class="ds-row">
              <span class="ds-row-label">Trial</span>
              <span class="ds-row-value">
                {#if selectedWorkspace.subscription.is_trial}
                  <span class="ds-chip ds-chip-warn">Yes</span>
                {:else}
                  No
                {/if}
              </span>
            </div>
          </div>
        </div>
      {:else}
        <div class="ds-card subscription-card empty">
          <h3 class="ds-card-title">No Active Subscription</h3>
          <p class="empty-hint">This workspace doesn't have an active subscription.</p>
        </div>
      {/if}

      <div class="forms-grid">
        {#if $canAccess.upgrades}
        <div class="ds-card form-card">
          <h3 class="ds-card-title">Upgrade Workspace</h3>
          <div class="ds-field">
            <label for="plan" class="ds-label">Plan</label>
            <select id="plan" bind:value={selectedPlanId} class="ds-select">
              {#each UPGRADE_PLAN_OPTIONS as plan}
                <option value={plan.value}>{plan.label}</option>
              {/each}
            </select>
          </div>
          <div class="ds-field">
            <label for="seats" class="ds-label">Seat Quantity</label>
            <input
              id="seats"
              type="number"
              min="1"
              bind:value={planQuantity}
              class="ds-input"
            />
          </div>
          <div class="ds-field">
            <label for="expires" class="ds-label">Expiration Date (optional)</label>
            <input
              id="expires"
              type="datetime-local"
              bind:value={expiresAt}
              class="ds-input"
            />
            <span class="form-hint">Leave empty for no expiration</span>
          </div>
          <button
            class="ds-btn ds-btn-primary submit-button"
            on:click={handleUpgrade}
            disabled={upgradeLoading}
          >
            {#if upgradeLoading}
              <span class="btn-spinner"></span>
              Upgrading...
            {:else}
              Upgrade Workspace
            {/if}
          </button>
        </div>
        {/if}

        <div class="ds-card form-card">
          <h3 class="ds-card-title">Extend Trial</h3>
          <div class="shortcut-row">
            {#each SHORTCUTS as sc}
              <button
                type="button"
                class="shortcut-button"
                on:click={() => extendTrialBy(sc.body)}
                disabled={trialLoading}
              >
                {sc.label}
              </button>
            {/each}
          </div>
          <div class="ds-field">
            <label class="ds-label">Extension Method</label>
            <div class="radio-group">
              <label class="radio-label">
                <input type="radio" bind:group={extendMode} value="days" />
                By Days
              </label>
              <label class="radio-label">
                <input type="radio" bind:group={extendMode} value="date" />
                Specific Date
              </label>
            </div>
          </div>
          {#if extendMode === "days"}
            <div class="ds-field">
              <label for="days" class="ds-label">Days to Extend</label>
              <input
                id="days"
                type="number"
                min="1"
                bind:value={extendDays}
                class="ds-input"
              />
            </div>
          {:else}
            <div class="ds-field">
              <label for="extendDate" class="ds-label">New Expiration Date</label>
              <input
                id="extendDate"
                type="datetime-local"
                bind:value={extendDate}
                class="ds-input"
              />
            </div>
          {/if}
          <button
            class="ds-btn ds-btn-primary submit-button"
            on:click={handleExtendTrial}
            disabled={trialLoading}
          >
            {#if trialLoading}
              <span class="btn-spinner"></span>
              Extending...
            {:else}
              Extend Trial
            {/if}
          </button>
        </div>
      </div>
    {/if}
  {/if}
</div>

<style>
  .container {
    padding: 2rem;
    max-width: 1000px;
    margin: 0 auto;
  }

  .dismiss-btn {
    background: none;
    border: none;
    font-weight: 500;
    cursor: pointer;
    opacity: 0.7;
    color: inherit;
  }

  .dismiss-btn:hover {
    opacity: 1;
  }

  .search-section {
    padding: 2rem;
  }

  .search-box {
    position: relative;
  }

  .search-spinner {
    position: absolute;
    right: 1rem;
    top: 50%;
    transform: translateY(-50%);
    width: 1.25rem;
    height: 1.25rem;
    border: 2px solid var(--ds-accent-soft);
    border-radius: 50%;
    border-top-color: var(--ds-accent);
    animation: spin 0.8s linear infinite;
  }

  .search-results {
    margin-top: 1rem;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  .result-item {
    width: 100%;
    padding: 0.85rem 1rem;
    border: 1px solid transparent;
    border-radius: var(--ds-radius-md);
    background: var(--ds-surface);
    text-align: left;
    cursor: pointer;
    transition: background 0.15s ease;
  }

  .result-item:hover {
    background: var(--ds-surface-2);
  }

  .result-title {
    font-weight: 600;
    color: var(--ds-text);
    margin-bottom: 0.25rem;
  }

  .result-meta {
    display: flex;
    gap: 1rem;
    font-size: 0.875rem;
    color: var(--ds-text-muted);
  }

  .result-id {
    font-family: var(--ds-mono);
  }

  .no-results {
    text-align: center;
    color: var(--ds-text-muted);
    padding: 2rem;
  }

  .back-button {
    margin-bottom: 1.5rem;
  }

  .loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 4rem;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  .workspace-card, .subscription-card {
    margin-bottom: 1.5rem;
  }

  .workspace-title-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;
    flex-wrap: wrap;
  }

  .workspace-title {
    font-size: 1rem;
    font-weight: 700;
    color: var(--ds-text);
    margin: 0;
    padding: 0;
    border: none;
  }

  .workspace-chips {
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;
  }

  .subscription-card.empty {
    color: var(--ds-text-muted);
  }

  .empty-hint {
    margin: 0;
    font-size: 0.875rem;
    color: var(--ds-text-muted);
  }

  .forms-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 1.5rem;
  }

  .form-hint {
    display: block;
    font-size: 0.75rem;
    color: var(--ds-text-muted);
    margin-top: 0.25rem;
  }

  .radio-group {
    display: flex;
    gap: 1.5rem;
  }

  .radio-label {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.9375rem;
    color: var(--ds-text);
    cursor: pointer;
  }

  .radio-label input[type="radio"] {
    cursor: pointer;
  }

  .submit-button {
    width: 100%;
    margin-top: 0.5rem;
  }

  .btn-spinner {
    width: 1rem;
    height: 1rem;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-radius: 50%;
    border-top-color: white;
    animation: spin 0.8s linear infinite;
  }

  .shortcut-row {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 16px;
  }
  .shortcut-button {
    padding: 6px 12px;
    border: 1px solid var(--ds-success);
    border-radius: var(--ds-radius-sm);
    background: transparent;
    color: var(--ds-success);
    cursor: pointer;
    font-size: 0.9rem;
    font-family: inherit;
  }
  .shortcut-button:hover:not(:disabled) {
    background: var(--ds-success);
    color: #fff;
  }
  .shortcut-button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    .container {
      padding: 1rem;
    }

    .forms-grid {
      grid-template-columns: 1fr;
    }
  }
</style>

